<template>
  <div>
    <div class="row">
      <div class="col-md-6">
        <div class="card cards-stats mb-4">
          <div class="card-header border-0 pb-0 d-flex justify-content-between">
            <div>
              <i class="fas fa-coins text-cr-red"></i> {{ $t('phrase.my_coins') }}
            </div>
            <div>
              <router-link
                class="btn btn-xs btn-default md-btn-flat"
                :to="{name: 'product-list'}"
              >
                <i class="fas fa-shopping-cart"></i> {{ $t('word.marketplace') }}
              </router-link>
            </div>
          </div>
          <div class="card-body text-center text-xlarge py-3">
            <strong>
              {{ user.coin_account.rate }}
            </strong>
          </div>
          <div class="card-footer border-0 small pt-0 d-flex justify-content-end">
            <router-link class="text-muted" :to="{name: 'commercial-list'}">
              {{ $t('sentence.review_more') }}
            </router-link>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card cards-stats mb-4">
          <div class="card-header border-0 pb-0">
            <i class="ion ion-md-checkbox-outline text-info"></i> {{ $t('word.reviews') }}
          </div>
          <div class="card-body text-center text-xlarge py-3">
            {{ reviewSessionsCount + researchReviewSessionsCount}}
          </div>
          <div class="card-footer border-0 small pt-0 d-flex justify-content-end">
            <span>
              {{ $t('phrase.my_reviews') }}
            </span>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div
          id="product-orders-card"
          class="card cards-stats bg-primary d-flex flex-column
           justify-content-center align-items-center text-white mb-4 clickable"
          @click="$router.push({name: 'product-list'})"
        >
          <div class="text-large card-header border-0 pb-0">
            <i class="fas fa-shopping-cart"></i>
          </div>
          <div
            class="text-large card-header border-0 pb-0 d-flex
             flex-column justify-content-center align-items-center"
          >
            {{ $t('word.marketplace') }}
          </div>
        </div>
      </div>
    </div>
    <div class="card mb-4" v-if="productOrders">
      <h6 class="card-header"><i class="fas fa-shopping-cart"></i>
        {{ $t('phrase.latest_orders') }}
      </h6>
      <div class="table-responsive" v-if="productOrders.length > 0">
        <table class="table card-table">
          <thead>
          <tr>
            <th colspan="2">{{ $t('word.product') }}</th>
            <th>{{ $t('phrase.number_of_coins') }}</th>
            <th>{{ $t('word.datetime') }}</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="order in productOrders" :key="order.timestamp">
            <td class="product-cell align-middle">
              <img
                class="w-100"
                :src="order.product.image_url"
                alt=""
              >
            </td>
            <td class="align-middle">
              {{ order.product.name }}
            </td>
            <td class="align-middle">
              {{ order.transaction.amount * -1 }}
            </td>
            <td class="align-middle">
              {{ stringToPrettyDateTime(order.timestamp) }}
            </td>
            <td class="align-middle">
              <span class="badge badge-success" v-if="order.processed">
                {{ $t('word.processed') }}
              </span>
              <span class="badge badge-warning" v-else>
                {{ $t('word.pending') }}
              </span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="card-body">
        {{ $t('sentence.no_product_orders_yet') }}
      </div>
    </div>

    <div class="card mb-3">
      <h6 class="card-header with-elements">
        <div class="card-header-title">
          <i class="fas fa-coins text-cr-red"></i>
          <span v-if="reviewSessions.length > 0">
            {{ $t('sentence.your_last_reviews_count', { reviewCount: reviewSessions.length }) }}
          </span>
          <span v-else>
            {{ $t('sentence.your_last_reviews') }}
          </span>
        </div>
      </h6>
      <div class="table-responsive">
        <table class="table card-table" v-if="reviewSessions.length > 0">
          <thead>
          <tr>
            <th>{{ $t('word.reviewed') }}</th>
            <th>{{ $tc('word.commercial', 1) }}</th>
            <th colspan="2">{{ $tc('word.organisation', 1) }}</th>
            <th>{{ $t('phrase.coins_earned') }}</th>
          </tr>
          </thead>
          <tbody>

          <tr v-for="session in allSessions" :key="session.uuid">
            <td>{{ stringToPrettyDateTime(session.reviewed) }}</td>
            <td>

              <router-link
                :to="{name: 'answers', params: {slug: session.commercial.slug}}"
                v-if="session.commercial"
              >
                {{ session.commercial.title }}
              </router-link>
              <router-link
                :to="{name: 'research-answers', params: { uuid: session.research.uuid }}"
                v-if="session.research"
              >
                {{session.research.title}}
              </router-link>
            </td>

            <td style="width: 20px">
              <router-link
                :to="getOrganisationParams(session)"
              >
                <img
                  :src="getOrganisationLogoUrl(session)"
                  class="organisation-logo-sm"
                  alt="org-logo"
                >
              </router-link>
            </td>
            <td>
              <router-link
                :to="getOrganisationParams(session)"
              >
                {{ getOrganisationName(session) }}
              </router-link>
            </td>

            <td>
              <i class="fas fa-coins text-cr-red"></i> {{ session.coins_earned }}
            </td>
          </tr>

          </tbody>
        </table>
        <div v-else class="card-body">
          {{ $t('sentence.no_reviews_yet') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/common/api';
import { stringToPrettyDateTime, toPrettyPrice } from '@/common/utils/formatting';
import { setBreadcrumbs } from '@/common/utils/breadcrumbs';
import { fetchResearchReviewSessions } from '@/reviewer/api';
import { mapGetters } from 'vuex';


export default {
  name: 'ReviewerDashboard',
  data() {
    return {
      productOrders: null,
      reviewSessions: null,
      reviewSessionsCount: null,
      researchReviewSessionsCount: null,
    };
  },
  computed: {
    ...mapGetters(['user']),
    allSessions() {
      const { reviewSessions } = this;
      if (!reviewSessions) {
        return [];
      }
      return reviewSessions.sort(
        (session1, session2) => (session1.reviewed > session2.reviewed ? -1 : 1),
      );
    },
  },
  methods: {
    stringToPrettyDateTime,
    toPrettyPrice,
    getOrganisationParams(session) {
      if (session.research) {
        return {
          name: 'organisation-detail',
          params: { slug: session.research.organisation.slug },
        };
      }
      return { name: 'organisation-detail', params: { slug: session.organisation.slug } };
    },
    getOrganisationName(session) {
      if (session.research) {
        return session.research.organisation.name;
      }
      return session.organisation.name;
    },
    getOrganisationLogoUrl(session) {
      if (session.research) {
        return session.research.organisation.logo_url;
      }
      return session.organisation.logo_url;
    },
    async initDashboard() {
      await this.fetchResearchReviewSessions();
      await this.fetchProductOrders();
    },
    async fetchResearchReviewSessions() {
      const response = await fetchResearchReviewSessions();
      this.researchReviewSessionsCount = response.data.count;
      this.reviewSessions = response.data.results;
    },
    async fetchProductOrders() {
      const response = await api.get('product-orders/');
      this.productOrders = response.data.results;
    },
  },
  created() {
    if (!this.user.coin_account.is_active) {
      this.$router.push({ name: 'activate-coin-account' });
      return;
    }
    this.initDashboard();
    setBreadcrumbs('dashboardReviewer');
  },
};
</script>


<style scoped>
.cards-stats {
  min-height: 152px;
}

.product-cell {
  width: 75px;
}

.organisation-logo-sm {
  width: 50px;
  height: auto;
}
</style>
